<template>
  <div
    v-if="submission"
    class="dt_sp_wrapper"
  >
    <b-row class="content-header sales_ch_custom_header">
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h1 class="custom-header-title">
              {{ submission.stringID }} - {{ submission.customerName }}
            </h1>
            <p class="log_info">
              Created by {{ submission.agentName || '–' }} on {{ dateFormatWithTime(submission.createdAt) }} <br>Last updated on {{ dateFormatWithTime(submission.updatedAt) }}
            </p>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <div class="submission-detail-body position-relative sp_submission_OP">
      <b-row class="align-items-start sps_rs_row">
        <b-col
          cols="12"
          md="8"
        >
          <b-button
            v-for="(item, index) in submission.items"
            :key="item._id"
            :variant="index === activeItemIndex ? 'primary' : 'outline-primary'"
            :class="['mr-1', 'mb-1', { 'submission-item-pill-inactive': index !== activeItemIndex }]"
            @click="activeItemIndex = index"
          >
            {{ item.stringID }}
          </b-button>
        </b-col>
        <b-col
          cols="12"
          md="4"
          class="text-md-right"
        >
          <b-button
            v-if="canViewThisAction('register-payment', 'SalesSubmission') && submission.itemsCount > 0 && (!submission.paymentMethod || (submission.paymentMethod === 'Counter' && submission.paymentStatus === 'counter-pending'))"
            variant="success"
            :to="{ name: 'sales-submissions-register-payment', params: { id: submission._id } }"
          >
            <feather-icon
              icon="DollarSignIcon"
              class="mr-50"
            />
            Register Payment
          </b-button>
        </b-col>
      </b-row>

      <h4 class="submission-item-title mb-1">
        {{ activeItem.stringID }} - {{ activeItem.productType }} {{ activeItem.lotNo ? `${activeItem.zoneNo}-${activeItem.lotNo}` : activeItem.packageName }}
      </h4>

      <b-tabs
        pills
        class="dashboard__tabs submission-detail-tabs"
      >
        <b-tab>
          <template #title>
            <b-card
              no-body
              class="dashboard__tab-card"
            >
              <b-card-body class="d-flex align-items-center">
                <b-avatar
                  variant="light-primary"
                  size="40"
                  rounded
                >
                  <feather-icon
                    size="20"
                    icon="UserIcon"
                  />
                </b-avatar>
                <div class="truncate ml-2">
                  <h2>Agent Details</h2>
                </div>
              </b-card-body>
            </b-card>
          </template>

          <b-card
            no-body
            class="dashboard__tab-content-card"
          >
            <b-tabs class="purchaser-tabs mb-2">
              <b-tab
                title="Sales Agent"
                active
              >
                <agent-detail-card
                  v-if="salesAgentDetail"
                  :agent-detail="salesAgentDetail"
                  :agent-type="salesAgentType"
                  role-label="Sales Agent"
                  permission-resource="SalesSubmission"
                  permission-action="change-agent"
                  @change-agent="showSalesAgentModal = true"
                />
                <span v-else>No sales agent on this submission</span>
              </b-tab>
              <b-tab title="Service Agent">
                <agent-detail-card
                  v-if="submission.serviceAgent"
                  :agent-detail="submission.serviceAgent"
                  agent-type="Agent"
                  role-label="Service Agent"
                  permission-resource="SalesSubmission"
                  permission-action="change-agent"
                  @change-agent="showServiceAgentModal = true"
                />
                <span v-else>No service agent on this submission</span>
              </b-tab>
            </b-tabs>
          </b-card>
        </b-tab>

        <b-tab>
          <template #title>
            <b-card
              no-body
              class="dashboard__tab-card"
            >
              <b-card-body class="d-flex align-items-center">
                <b-avatar
                  variant="light-primary"
                  size="40"
                  rounded
                >
                  <feather-icon
                    size="20"
                    icon="InfoIcon"
                  />
                </b-avatar>
                <div class="truncate ml-2">
                  <h2>Order Information</h2>
                </div>
              </b-card-body>
            </b-card>
          </template>

          <b-card
            no-body
            class="dashboard__tab-content-card"
          >
            <b-row class="match-height">
              <b-col
                cols="12"
                md="6"
              >
                <b-card class="has-padding order-info-card">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <b-card-title class="mb-0">
                      Purchaser Details
                    </b-card-title>
                    <b-button
                      v-if="canViewThisAction('edit-purchaser-details', 'SalesSubmission')"
                      variant="flat-primary"
                      @click="showPurchaserDetailsModal = true"
                    >
                      <feather-icon icon="EditIcon" /> Edit
                    </b-button>
                  </div>
                  <b-table-simple
                    borderless
                    responsive
                    class="particulars_of_deceased d-none d-md-block"
                  >
                    <b-tbody>
                      <b-tr>
                        <b-th width="230px">
                          Purchaser Name
                        </b-th>
                        <b-td>
                          <b-button
                            v-if="canViewThisAction('show', 'Customer') && submission.customer"
                            v-ripple.400="'rgba(30, 30, 30, 0.15)'"
                            variant="link"
                            class="p-0 weight-600 align-middle"
                            :to="{ name: 'customers-customers-show', params: { id: submission.customer._id } }"
                          >
                            {{ submission.customerName || '–' }}
                          </b-button>
                          <span v-else>{{ submission.customerName || '–' }}</span>
                        </b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          NRIC
                        </b-th>
                        <b-td>{{ submission.customerNric || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Date of Birth
                        </b-th>
                        <b-td>{{ submission.customerDob ? dateFormat(submission.customerDob) : '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Sex
                        </b-th>
                        <b-td>{{ submission.customerSex || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Race
                        </b-th>
                        <b-td>{{ submission.customerRace || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Nationality
                        </b-th>
                        <b-td>{{ submission.customerNationality || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Mobile No.
                        </b-th>
                        <b-td>{{ submission.customerContact || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Email Address
                        </b-th>
                        <b-td>{{ submission.customerEmail || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Permanent Address
                        </b-th>
                        <b-td>{{ permanentAddress || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Correspondence Address
                        </b-th>
                        <b-td>{{ correspondenceAddress || '–' }}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>
              </b-col>

              <b-col
                cols="12"
                md="6"
              >
                <b-card class="has-padding order-info-card">
                  <b-card-title class="mb-2">
                    Order Item Details
                  </b-card-title>
                  <b-table-simple
                    borderless
                    responsive
                    class="particulars_of_deceased d-none d-md-block"
                  >
                    <b-tbody>
                      <b-tr>
                        <b-th width="230px">
                          Product Type
                        </b-th>
                        <b-td>{{ activeItem.productType || '–' }}</b-td>
                      </b-tr>
                      <template v-if="activeItem.lotNo">
                        <b-tr>
                          <b-th width="230px">
                            Zone No.
                          </b-th>
                          <b-td>{{ activeItem.zoneNo || '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Lot No.
                          </b-th>
                          <b-td>{{ activeItem.lotNo || '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Lot Type
                          </b-th>
                          <b-td>{{ activeItem.lotType || '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Bury Capacity
                          </b-th>
                          <b-td>{{ activeItem.lot && activeItem.lot.buryCapacity ? activeItem.lot.buryCapacity : '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Area Length
                          </b-th>
                          <b-td>{{ activeItem.lot && activeItem.lot.size ? activeItem.lot.size : '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Direction
                          </b-th>
                          <b-td>{{ activeItem.lot && activeItem.lot.direction ? activeItem.lot.direction : '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Side
                          </b-th>
                          <b-td>–</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Level No.
                          </b-th>
                          <b-td>{{ activeItem.lot && activeItem.lot.levelNo ? activeItem.lot.levelNo : '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Section No.
                          </b-th>
                          <b-td>{{ activeItem.lot && activeItem.lot.sectionNo ? activeItem.lot.sectionNo : '–' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Earliest Interment Date
                          </b-th>
                          <b-td>{{ activeItem.lot && activeItem.lot.intermentDate ? dateFormat(activeItem.lot.intermentDate) : 'Immediate' }}</b-td>
                        </b-tr>
                      </template>
                      <b-tr v-else>
                        <b-th width="230px">
                          Package
                        </b-th>
                        <b-td>{{ (activeItem.productCatalog && activeItem.productCatalog.name) || activeItem.packageName || '–' }}</b-td>
                      </b-tr>
                      <b-tr v-if="activeItem.productType !== 'Urn'">
                        <b-th width="230px">
                          Need Type
                        </b-th>
                        <b-td>{{ activeItem.worshipPeriod || needTypeLabel(activeItem.needType) }}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>
              </b-col>
            </b-row>
          </b-card>
        </b-tab>

        <b-tab>
          <template #title>
            <b-card
              no-body
              class="dashboard__tab-card"
            >
              <b-card-body class="d-flex align-items-center">
                <b-avatar
                  variant="light-primary"
                  size="40"
                  rounded
                >
                  <feather-icon
                    size="20"
                    icon="DollarSignIcon"
                  />
                </b-avatar>
                <div class="truncate ml-2">
                  <h2>Payment Information</h2>
                </div>
              </b-card-body>
            </b-card>
          </template>

          <b-card
            no-body
            class="dashboard__tab-content-card so_payment_info_wrap"
          >
            <b-row class="match-height payment-info-top-row">
              <b-col
                cols="12"
                md="4"
              >
                <b-card class="has-padding">
                  <b-card-title class="mb-2">
                    Purchase Option
                  </b-card-title>
                  <b-table-simple
                    borderless
                    responsive
                    class="particulars_of_deceased d-none d-md-block"
                  >
                    <b-tbody>
                      <b-tr>
                        <b-th width="150px">
                          Purchase Option
                        </b-th>
                        <b-td>{{ purchaseOptionLabel(activeItem.paymentType) }}</b-td>
                      </b-tr>
                      <b-tr v-if="activeItem.paymentType === 'downpayment'">
                        <b-th width="150px">
                          Downpayment
                        </b-th>
                        <b-td>{{ activeItem.downPaymentPercentage }}%</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>

                <b-card class="has-padding mb-0">
                  <b-card-title class="mb-2 d-flex justify-content-between align-items-center">
                    <span>Discounts</span>
                    <b-button
                      v-if="canViewThisAction('edit-discounts', 'SalesSubmission')"
                      variant="flat-primary"
                      size="sm"
                      @click="showPromotionsModal = true"
                    >
                      <feather-icon icon="EditIcon" /> Edit
                    </b-button>
                  </b-card-title>
                  <b-table-simple
                    borderless
                    responsive
                  >
                    <b-tbody>
                      <b-tr>
                        <b-td class="font-weight-bold">
                          Discount Type
                        </b-td>
                        <b-td class="font-weight-bold">
                          Discount Amount
                        </b-td>
                      </b-tr>
                      <b-tr
                        v-for="promotion in activeItem.promotions"
                        :key="promotion._id"
                      >
                        <b-td>
                          <div class="text-primary">
                            {{ promotion.name }}
                          </div>
                          <small>{{ promotion.couponCode }}</small>
                        </b-td>
                        <b-td>
                          <div>-${{ numberFormat(promotion.amount) }}</div>
                          <small :class="promotion.requiredBdVerification ? 'text-warning' : 'text-muted'">
                            {{ promotion.requiredBdVerification ? 'Verification needed' : 'Auto-applied' }}
                          </small>
                        </b-td>
                      </b-tr>
                      <b-tr v-if="!activeItem.promotions || !activeItem.promotions.length">
                        <b-td colspan="2">
                          No discounts applied
                        </b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >
                <b-card class="has-padding h-100 mb-0">
                  <b-card-title class="mb-2">
                    Price Details
                  </b-card-title>
                  <b-table-simple
                    borderless
                    responsive
                    class="particulars_of_deceased d-none d-md-block"
                  >
                    <b-tbody>
                      <b-tr v-if="['Funeral Service Package', 'Urn'].includes(activeItem.productType)">
                        <b-th width="150px">
                          Product Price
                        </b-th>
                        <b-td>${{ numberFormat(activeItem.productPrice) }}</b-td>
                      </b-tr>
                      <template v-else>
                        <b-tr>
                          <b-th width="150px">
                            Lot Price
                          </b-th>
                          <b-td>${{ numberFormat(activeItem.productPrice) }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="150px">
                            Maintenance Fee
                          </b-th>
                          <b-td>${{ numberFormat(activeItem.maintenanceFee) }}</b-td>
                        </b-tr>
                      </template>
                      <b-tr
                        v-for="promotion in activeItem.promotions"
                        :key="promotion._id"
                      >
                        <b-th width="150px">
                          {{ promotion.name }}
                        </b-th>
                        <b-td>-${{ numberFormat(promotion.amount) }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-td
                          colspan="2"
                          class="p-0"
                        >
                          <hr class="my-50">
                        </b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="150px">
                          Subtotal
                        </b-th>
                        <b-td>${{ numberFormat(itemSubtotal) }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="150px">
                          GST {{ activeItem.gstPercentage }}%
                        </b-th>
                        <b-td>${{ numberFormat(activeItem.gstAmount) }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-td
                          colspan="2"
                          class="p-0"
                        >
                          <hr class="my-50">
                        </b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="150px">
                          Order Total
                        </b-th>
                        <b-td>${{ numberFormat(itemOrderTotal) }}</b-td>
                      </b-tr>
                      <b-tr v-if="activeItem.paymentType === 'downpayment'">
                        <b-th width="150px">
                          Downpayment
                        </b-th>
                        <b-td>${{ numberFormat(activeItem.downPayment) }}</b-td>
                      </b-tr>
                      <b-tr v-if="activeItem.paymentType === 'downpayment'">
                        <b-th width="150px">
                          Balance
                        </b-th>
                        <b-td>${{ numberFormat(itemSubtotal - activeItem.downPayment) }}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >
                <b-card class="has-padding h-100 mb-0">
                  <b-card-title class="mb-2 d-flex justify-content-between align-items-center">
                    <span>Installment Details</span>
                    <b-button
                      v-if="activeItem.paymentType === 'downpayment' && canViewThisAction('edit-installment', 'SalesSubmission')"
                      variant="flat-primary"
                      size="sm"
                      @click="showInstallmentModal = true"
                    >
                      <feather-icon icon="EditIcon" /> Edit
                    </b-button>
                  </b-card-title>
                  <p
                    v-if="activeItem.paymentType !== 'downpayment'"
                    class="text-muted mb-0"
                  >
                    No installment plan for this item
                  </p>
                  <b-table-simple
                    v-else
                    borderless
                    responsive
                    class="particulars_of_deceased d-none d-md-block"
                  >
                    <b-tbody>
                      <b-tr>
                        <b-th width="150px">
                          Installment Amount
                        </b-th>
                        <b-td>${{ numberFormat(activeItem.installmentAmount) }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="150px">
                          Tenure
                        </b-th>
                        <b-td>{{ activeItem.installmentTenure }} months</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="150px">
                          Mode of Payment
                        </b-th>
                        <b-td>{{ activeItem.installmentMode || '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-td
                          colspan="2"
                          class="p-0"
                        >
                          <hr class="my-50">
                        </b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="150px">
                          Installment Due Date
                        </b-th>
                        <b-td>{{ activeItem.installmentDueDate ? dateFormat(activeItem.installmentDueDate) : '–' }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="150px">
                          Monthly Installment
                        </b-th>
                        <b-td>${{ numberFormat(activeItem.monthlyInstallment) }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="150px">
                          Final Installment
                        </b-th>
                        <b-td>${{ numberFormat(activeItem.finalInstallment) }}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>
              </b-col>
            </b-row>

            <b-row class="payment-info-bottom-row">
              <b-col cols="12">
                <b-card
                  no-body
                  class="has-padding mt-2 tab-bottom-card"
                >
                  <b-card-body>
                    <b-card-title class="mb-2">
                      Payment Information
                    </b-card-title>
                    <b-table-simple
                      responsive
                      class="payment-history-table"
                    >
                      <b-thead>
                        <b-tr>
                          <b-th>Trx No.</b-th>
                          <b-th>Payment Mode</b-th>
                          <b-th>Amount</b-th>
                          <b-th>Payment Reference</b-th>
                          <b-th>Date/Time</b-th>
                          <b-th>Processed By</b-th>
                        </b-tr>
                      </b-thead>
                      <b-tbody>
                        <b-tr
                          v-for="payment in activeItem.payments"
                          :key="payment._id"
                        >
                          <b-td>{{ payment.trxNo || '–' }}</b-td>
                          <b-td>{{ payment.paymentMode || '–' }}</b-td>
                          <b-td>${{ numberFormat(payment.amount) }}</b-td>
                          <b-td>{{ payment.chargeID || '–' }}</b-td>
                          <b-td>{{ payment.paymentDate ? dateFormatWithTime(payment.paymentDate) : '–' }}</b-td>
                          <b-td>
                            <b-button
                              v-if="payment.processedBy && canViewThisAction('show', 'User')"
                              variant="link"
                              class="p-0 weight-600 align-middle"
                              :to="{ name: 'directory-users-show', params: { id: payment.processedBy._id } }"
                            >
                              {{ payment.processedBy.name }}
                            </b-button>
                            <span v-else>{{ payment.processedBy ? payment.processedBy.name : '–' }}</span>
                          </b-td>
                        </b-tr>
                        <b-tr v-if="!activeItem.payments || !activeItem.payments.length">
                          <b-td colspan="6">
                            <div class="mt-1">
                              No payments recorded yet
                            </div>
                          </b-td>
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-card>
        </b-tab>

        <b-tab>
          <template #title>
            <b-card
              no-body
              class="dashboard__tab-card"
            >
              <b-card-body class="d-flex align-items-center">
                <b-avatar
                  variant="light-primary"
                  size="40"
                  rounded
                >
                  <feather-icon
                    size="20"
                    icon="FileTextIcon"
                  />
                </b-avatar>
                <div class="truncate ml-2">
                  <h2>Information & Documents</h2>
                </div>
              </b-card-body>
            </b-card>
          </template>

          <b-card
            no-body
            class="dashboard__tab-content-card info_document_warp"
          >
            <b-row class="match-height payment-info-top-row">
              <b-col
                cols="12"
                md="6"
              >
                <b-card class="has-padding h-100 mb-0">
                  <b-card-title class="mb-2 d-flex justify-content-between align-items-center">
                    <span>Required Information</span>
                    <b-button
                      v-if="canViewThisAction('edit-required-information', 'SalesSubmission')"
                      variant="flat-primary"
                      size="sm"
                      @click="showRequiredInfoModal = true"
                    >
                      <feather-icon icon="EditIcon" /> Edit
                    </b-button>
                  </b-card-title>
                  <b-table-simple
                    borderless
                    responsive
                    class="particulars_of_deceased d-none d-md-block"
                  >
                    <b-tbody>
                      <b-tr v-if="activeItem.productType === 'Little Buddha'">
                        <b-th width="230px">
                          End Users
                        </b-th>
                        <b-td>
                          <span v-if="activeItem.endUsers && activeItem.endUsers.length">
                            <span
                              v-for="(user, index) in activeItem.endUsers"
                              :key="user._id"
                            >#{{ index + 1 }}. {{ user.name }}<span v-if="index < activeItem.endUsers.length - 1">, </span></span>
                          </span>
                          <span v-else>–</span>
                        </b-td>
                      </b-tr>
                      <b-tr>
                        <b-th width="230px">
                          Personal Data Consent
                        </b-th>
                        <b-td>{{ activeItem.personalDataConsent ? 'Yes, I agree' : 'No' }}</b-td>
                      </b-tr>
                      <template v-if="activeItem.productType !== 'Little Buddha'">
                        <b-tr>
                          <b-th width="230px">
                            Purchase Declaration
                          </b-th>
                          <b-td>{{ activeItem.purchaseDeclaration ? 'Yes, I agree' : 'No' }}</b-td>
                        </b-tr>
                        <b-tr>
                          <b-th width="230px">
                            Agent Self-Purchase Declaration
                          </b-th>
                          <b-td>{{ activeItem.agentSelfPurchase ? 'Yes, I agree' : 'No' }}</b-td>
                        </b-tr>
                      </template>
                      <b-tr>
                        <b-th width="230px">
                          Marketing Consent
                        </b-th>
                        <b-td>{{ activeItem.marketingDataConsent ? 'I want to receive advertising and promotional material' : 'No' }}</b-td>
                      </b-tr>
                      <b-tr v-if="activeItem.productType === 'Little Buddha'">
                        <b-th width="230px">
                          Worship Period
                        </b-th>
                        <b-td>{{ activeItem.worshipPeriodStart && activeItem.worshipPeriodEnd ? `${dateFormat(activeItem.worshipPeriodStart)} - ${dateFormat(activeItem.worshipPeriodEnd)}` : '–' }}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </b-card>
              </b-col>

              <b-col
                cols="12"
                md="6"
              >
                <b-card class="has-padding h-100 mb-0">
                  <b-card-title class="mb-2">
                    Related Nominees
                  </b-card-title>
                  <b-tabs class="purchaser-tabs">
                    <b-tab :title="`Intended User (${(activeItem.intendedUsers || []).length})`">
                      <b-row
                        v-if="activeItem.intendedUsers && activeItem.intendedUsers.length"
                        class="match-height"
                      >
                        <b-col
                          v-for="(user, index) in activeItem.intendedUsers"
                          :key="user._id"
                          md="6"
                        >
                          <b-card
                            class="card-transaction iu-card"
                            no-body
                          >
                            <b-card-header class="no-bottom-border">
                              <b-card-title>Intended User #{{ index + 1 }}</b-card-title>
                              <b-button
                                variant="none"
                                class="btn-icon rounded-circle ml-auto mr-1 flat-primary-bg"
                              >
                                <feather-icon icon="UserIcon" />
                              </b-button>
                              <b-dropdown
                                v-if="canViewThisAction('edit-nominee', 'SalesSubmission')"
                                variant="link"
                                no-caret
                                class="chart-dropdown"
                                toggle-class="p-0"
                                right
                              >
                                <template #button-content>
                                  <feather-icon
                                    icon="MoreVerticalIcon"
                                    size="18"
                                    class="text-body cursor-pointer"
                                  />
                                </template>
                                <b-dropdown-item @click="openIntendedUserModal(user)">
                                  <feather-icon icon="EditIcon" />
                                  <span class="align-middle ml-50">Edit</span>
                                </b-dropdown-item>
                              </b-dropdown>
                            </b-card-header>
                            <b-card-body>
                              <span class="text-bold-black align-middle text-uppercase">
                                {{ user.name || '–' }}
                              </span>
                              <b-badge
                                v-if="user.isPurchaser"
                                variant="light-info"
                                class="ml-50"
                              >
                                Purchaser
                              </b-badge>
                              <br>
                              <div v-if="isNricRevealed(user._id)">
                                <span class="text-uppercase mr-50">{{ user.nric || '–' }}</span>
                                <b-button
                                  variant="primary"
                                  size="sm"
                                  class="px-50"
                                  @click="hideNric(user._id)"
                                >
                                  Hide full NRIC
                                </b-button>
                              </div>
                              <div v-else>
                                <span class="text-uppercase mr-50">{{ user.encryptedNric || '–' }}</span>
                                <b-button
                                  v-if="canViewThisAction('view-nric', 'NRIC') && user.nric"
                                  variant="primary"
                                  size="sm"
                                  class="px-50"
                                  @click="revealNric(user._id)"
                                >
                                  Show full NRIC
                                </b-button>
                              </div>
                              <span class="text-uppercase">{{ user.relation || '–' }}</span>
                              <br>
                              <span>
                                <b-img
                                  height="22"
                                  :src="require('@/assets/images/admin/birthday.png')"
                                  alt="birthday"
                                  class="mr-25"
                                />
                                {{ user.dob ? dateFormat(user.dob) : '–' }}
                              </span>
                              <br>
                              <b-badge
                                v-if="user.singpass"
                                variant="secondary"
                                class="mt-50 singpass-badge"
                              >
                                via Singpass
                              </b-badge>
                            </b-card-body>
                          </b-card>
                        </b-col>
                      </b-row>
                      <div
                        v-else
                        class="empty_block"
                      >
                        <h4>No Data Yet</h4>
                        <span class="align-middle">Item will show once the data is entered</span>
                      </div>
                    </b-tab>
                    <b-tab :title="`Authorised Person (${(activeItem.authorizedPersons || []).length})`">
                      <b-row
                        v-if="activeItem.authorizedPersons && activeItem.authorizedPersons.length"
                        class="match-height"
                      >
                        <b-col
                          v-for="(person, index) in activeItem.authorizedPersons"
                          :key="person._id"
                          md="6"
                        >
                          <b-card
                            class="card-transaction iu-card"
                            no-body
                          >
                            <b-card-header class="no-bottom-border">
                              <b-card-title>Authorised Person #{{ index + 1 }}</b-card-title>
                              <b-button
                                variant="none"
                                class="btn-icon rounded-circle ml-auto mr-1 flat-primary-bg"
                              >
                                <feather-icon icon="UserIcon" />
                              </b-button>
                              <b-dropdown
                                v-if="canViewThisAction('edit-nominee', 'SalesSubmission')"
                                variant="link"
                                no-caret
                                class="chart-dropdown"
                                toggle-class="p-0"
                                right
                              >
                                <template #button-content>
                                  <feather-icon
                                    icon="MoreVerticalIcon"
                                    size="18"
                                    class="text-body cursor-pointer"
                                  />
                                </template>
                                <b-dropdown-item @click="openAuthorizedPersonModal(person)">
                                  <feather-icon icon="EditIcon" />
                                  <span class="align-middle ml-50">Edit</span>
                                </b-dropdown-item>
                              </b-dropdown>
                            </b-card-header>
                            <b-card-body>
                              <span class="text-bold-black align-middle text-uppercase">
                                {{ person.name || '–' }}
                              </span>
                              <br>
                              <div v-if="isNricRevealed(person._id)">
                                <span class="text-uppercase mr-50">{{ person.nric || '–' }}</span>
                                <b-button
                                  variant="primary"
                                  size="sm"
                                  class="px-50"
                                  @click="hideNric(person._id)"
                                >
                                  Hide full NRIC
                                </b-button>
                              </div>
                              <div v-else>
                                <span class="text-uppercase mr-50">{{ person.encryptedNric || '–' }}</span>
                                <b-button
                                  v-if="canViewThisAction('view-nric', 'NRIC') && person.nric"
                                  variant="primary"
                                  size="sm"
                                  class="px-50"
                                  @click="revealNric(person._id)"
                                >
                                  Show full NRIC
                                </b-button>
                              </div>
                              <span class="text-uppercase">{{ person.relation || '–' }}</span>
                              <br>
                              <span>
                                <feather-icon
                                  icon="PhoneIcon"
                                  size="18"
                                  class="mr-25 iu-icon"
                                />
                                {{ person.contact || '–' }}
                              </span>
                              <br>
                              <b-badge
                                v-if="person.singpass"
                                variant="secondary"
                                class="mt-50 singpass-badge"
                              >
                                via Singpass
                              </b-badge>
                            </b-card-body>
                          </b-card>
                        </b-col>
                      </b-row>
                      <div
                        v-else
                        class="empty_block"
                      >
                        <h4>No Data Yet</h4>
                        <span class="align-middle">Item will show once the data is entered</span>
                      </div>
                    </b-tab>
                  </b-tabs>
                </b-card>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12">
                <b-card
                  no-body
                  class="has-padding mt-2 tab-bottom-card"
                >
                  <b-card-body>
                    <b-card-title class="mb-2">
                      Documents
                    </b-card-title>
                    <b-table-simple
                      responsive
                      class="payment-history-table"
                    >
                      <b-thead>
                        <b-tr>
                          <b-th>No.</b-th>
                          <b-th>Document</b-th>
                          <b-th>Action</b-th>
                        </b-tr>
                      </b-thead>
                      <b-tbody>
                        <b-tr
                          v-for="(file, index) in activeItem.attachments"
                          :key="file._id"
                        >
                          <b-td>{{ index + 1 }}</b-td>
                          <b-td>
                            <div class="d-flex align-items-center justify-content-between">
                              <span>
                                <b-link
                                  v-if="file.fileStorage"
                                  :to="{ name: 'file-view', params: { id: file.fileStorage } }"
                                  target="_blank"
                                >
                                  {{ file.name }}
                                </b-link>
                                <span v-else>{{ file.name }}</span>
                              </span>
                              <span>
                                <b-badge
                                  v-if="file.isForm"
                                  variant="light-success"
                                  class="text-capitalize"
                                >
                                  Signed
                                </b-badge>
                                <span
                                  v-if="file.signDate"
                                  class="ml-50"
                                >
                                  On {{ dateFormatWithTime(file.signDate) }}
                                </span>
                              </span>
                            </div>
                          </b-td>
                          <b-td>
                            <b-dropdown
                              variant="link"
                              no-caret
                              right
                            >
                              <template #button-content>
                                <feather-icon
                                  icon="MoreVerticalIcon"
                                  class="text-body"
                                />
                              </template>
                              <b-dropdown-item
                                v-if="file.fileStorage"
                                :to="{ name: 'file-view', params: { id: file.fileStorage } }"
                                target="_blank"
                              >
                                <feather-icon icon="EyeIcon" />
                                <span class="align-middle ml-50">View</span>
                              </b-dropdown-item>
                            </b-dropdown>
                          </b-td>
                        </b-tr>
                        <b-tr v-if="!activeItem.attachments || !activeItem.attachments.length">
                          <b-td colspan="3">
                            <div class="mt-1">
                              No documents yet
                            </div>
                          </b-td>
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-card>
        </b-tab>
      </b-tabs>
    </div>

    <required-information-edit-modal
      :visible.sync="showRequiredInfoModal"
      :is-little-buddha="activeItem.productType === 'Little Buddha'"
      :personal-data-consent="activeItem.personalDataConsent"
      :purchase-declaration="activeItem.purchaseDeclaration"
      :agent-self-purchase="activeItem.agentSelfPurchase"
      :marketing-data-consent="activeItem.marketingDataConsent"
      :names="(activeItem.endUsers || []).map(user => user.name)"
      :worship-period-start="activeItem.worshipPeriodStart ? dateFormat(activeItem.worshipPeriodStart) : null"
      :worship-period-end="activeItem.worshipPeriodEnd ? dateFormat(activeItem.worshipPeriodEnd) : null"
      :saving="savingRequiredInfo"
      @save="saveRequiredInformation"
    />

    <service-agent-change-modal
      :visible.sync="showServiceAgentModal"
      :saving="savingServiceAgent"
      :agency-code="submission.serviceAgent ? submission.serviceAgent.agencyCode : null"
      @save="saveServiceAgent"
    />

    <service-agent-change-modal
      :visible.sync="showSalesAgentModal"
      :saving="savingSalesAgent"
      :agency-code="submission.mainAgent ? submission.mainAgent.agencyCode : (submission.agent && submission.agent.agent ? submission.agent.agent.agencyCode : null)"
      @save="saveSalesAgent"
    />

    <purchaser-details-edit-modal
      :visible.sync="showPurchaserDetailsModal"
      :saving="savingPurchaserDetails"
      :singpass="submission.singpass"
      :email-verified="submission.customer ? submission.customer.emailVerified : false"
      :customer-id="submission.customer ? submission.customer._id : null"
      :customer-name="submission.customerName"
      :customer-nric="submission.customerNric"
      :customer-dob="submission.customerDob ? dateFormat(submission.customerDob) : null"
      :customer-sex="submission.customerSex"
      :customer-race="submission.customerRace"
      :customer-nationality="submission.customerNationality"
      :customer-contact="submission.customerContact"
      :customer-email="submission.customerEmail"
      :per-address1="submission.perAddress1"
      :per-address2="submission.perAddress2"
      :per-postcode="submission.perPostcode"
      :per-country="submission.perCountry"
      :cor-address1="submission.corAddress1"
      :cor-address2="submission.corAddress2"
      :cor-postcode="submission.corPostcode"
      :cor-country="submission.corCountry"
      @save="savePurchaserDetails"
    />

    <intended-user-edit-modal
      :visible.sync="showIntendedUserModal"
      :saving="savingIntendedUser"
      :singpass="editingIntendedUser ? editingIntendedUser.singpass : false"
      :name="editingIntendedUser ? editingIntendedUser.name : ''"
      :nric="editingIntendedUser ? editingIntendedUser.nric : ''"
      :dob="editingIntendedUser && editingIntendedUser.dob ? dateFormat(editingIntendedUser.dob) : null"
      :relation="editingIntendedUser ? editingIntendedUser.relation : ''"
      @save="saveIntendedUser"
    />

    <authorized-person-edit-modal
      :visible.sync="showAuthorizedPersonModal"
      :saving="savingAuthorizedPerson"
      :singpass="editingAuthorizedPerson ? editingAuthorizedPerson.singpass : false"
      :name="editingAuthorizedPerson ? editingAuthorizedPerson.name : ''"
      :nric="editingAuthorizedPerson ? editingAuthorizedPerson.nric : ''"
      :contact="editingAuthorizedPerson ? editingAuthorizedPerson.contact : ''"
      :relation="editingAuthorizedPerson ? editingAuthorizedPerson.relation : ''"
      @save="saveAuthorizedPerson"
    />

    <installment-edit-modal
      :visible.sync="showInstallmentModal"
      :saving="savingInstallment"
      :installment-amount="activeItem.installmentAmount"
      :installment-tenure="activeItem.installmentTenure"
      :installment-mode="activeItem.installmentMode"
      :monthly-installment="activeItem.monthlyInstallment"
      :final-installment="activeItem.finalInstallment"
      :installment-due-date="activeItem.installmentDueDate ? dateFormat(activeItem.installmentDueDate) : null"
      @save="saveInstallment"
    />

    <promotions-edit-modal
      :visible.sync="showPromotionsModal"
      :saving="savingPromotions"
      :promotions="activeItem.promotions"
      :product-price="activeItem.productPrice"
      @save="savePromotions"
    />
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardTitle, BCardBody, BCardHeader, BTableSimple, BThead, BTbody, BTr, BTh, BTd, BBadge,
  BTabs, BTab, BAvatar, BButton, BLink, BDropdown, BDropdownItem, BImg,
} from 'bootstrap-vue'
import Vue from 'vue'
import { ref, onUnmounted, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import Ripple from 'vue-ripple-directive'
import router from '@/router/index'
import store from '@/store'
import { useUtils as useAclUtils } from '@/libs/acl/custom'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { resolvePaymentStatusVariant, resolvePaymentStatusLabel } from './paymentStatus'
import submissionStoreModule from './submissionStoreModule'
import AgentDetailCard from './AgentDetailCard.vue'
import RequiredInformationEditModal from '../shared/RequiredInformationEditModal.vue'
import ServiceAgentChangeModal from '../shared/ServiceAgentChangeModal.vue'
import PurchaserDetailsEditModal from '../shared/PurchaserDetailsEditModal.vue'
import IntendedUserEditModal from '../shared/IntendedUserEditModal.vue'
import AuthorizedPersonEditModal from '../shared/AuthorizedPersonEditModal.vue'
import InstallmentEditModal from '../shared/InstallmentEditModal.vue'
import PromotionsEditModal from '../shared/PromotionsEditModal.vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BCardBody,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BBadge,
    BTabs,
    BTab,
    BAvatar,
    BButton,
    BLink,
    BDropdown,
    BDropdownItem,
    BCardHeader,
    BImg,
    AgentDetailCard,
    RequiredInformationEditModal,
    ServiceAgentChangeModal,
    PurchaserDetailsEditModal,
    IntendedUserEditModal,
    AuthorizedPersonEditModal,
    InstallmentEditModal,
    PromotionsEditModal,
  },
  directives: {
    Ripple,
  },
  setup() {
    const SALES_SUBMISSION_APP_STORE_MODULE_NAME = 'app-sales-submissions'

    if (!store.hasModule(SALES_SUBMISSION_APP_STORE_MODULE_NAME)) store.registerModule(SALES_SUBMISSION_APP_STORE_MODULE_NAME, submissionStoreModule)
    onUnmounted(() => {
      if (store.hasModule(SALES_SUBMISSION_APP_STORE_MODULE_NAME)) store.unregisterModule(SALES_SUBMISSION_APP_STORE_MODULE_NAME)
    })

    const { canViewThisAction } = useAclUtils()

    const submission = ref(null)
    const activeItemIndex = ref(0)

    // Array (not Set) - Vue 2's reactivity doesn't track Set mutations, but push/splice on a
    // ref([]) are patched and do trigger re-render.
    const revealedNricIds = ref([])
    const isNricRevealed = id => revealedNricIds.value.includes(id)
    const revealNric = id => { if (!revealedNricIds.value.includes(id)) revealedNricIds.value.push(id) }
    const hideNric = id => {
      const index = revealedNricIds.value.indexOf(id)
      if (index > -1) revealedNricIds.value.splice(index, 1)
    }

    store.dispatch('app-sales-submissions/fetchSubmission', router.currentRoute.params.id)
      .then(response => {
        submission.value = response.data

        const bcLength = store.state.breadcrumb.breadcrumbs.length
        if (bcLength > 1) {
          store.commit('breadcrumb/REMOVE_BREADCRUMB')
        }
        store.commit('breadcrumb/UPDATE_BREADCRUMB', {
          active: true,
          text: `${submission.value.stringID} - ${submission.value.customerName}`,
        })
      })

    const activeItem = computed(() => (submission.value && submission.value.items[activeItemIndex.value]) || {})

    const addressLine = (line1, line2, postcode, country) => [line1, line2, postcode, country].filter(Boolean).join(', ')
    const permanentAddress = computed(() => (submission.value
      ? addressLine(submission.value.perAddress1, submission.value.perAddress2, submission.value.perPostcode, submission.value.perCountry)
      : ''))
    const correspondenceAddress = computed(() => (submission.value
      ? addressLine(submission.value.corAddress1, submission.value.corAddress2, submission.value.corPostcode, submission.value.corCountry)
      : ''))

    // The submission's own submitting agent - mainAgent (a confirmed Agent) or agent (still a
    // ProbationaryAgent), mutually exclusive. Drives which AgentDetailCard field layout to use.
    const salesAgentDetail = computed(() => (submission.value ? (submission.value.mainAgent || submission.value.agent) : null))
    const salesAgentType = computed(() => (submission.value && submission.value.mainAgent ? 'Agent' : 'ProbationaryAgent'))

    // Promotions net of the item's own price+fee gets the subtotal before GST; GST is computed on
    // that subtotal at save time, so order total = subtotal + the item's own gstAmount.
    const discountsTotal = item => (item.promotions || [])
      .reduce((sum, d) => sum + (Number(d.amount) || 0), 0)
    const itemSubtotal = computed(() => {
      if (!activeItem.value) return 0
      return (Number(activeItem.value.productPrice) || 0) + (Number(activeItem.value.maintenanceFee) || 0) - discountsTotal(activeItem.value)
    })
    const itemOrderTotal = computed(() => itemSubtotal.value + (Number(activeItem.value.gstAmount) || 0))

    const purchaseOptionLabel = paymentType => {
      if (paymentType === 'downpayment') return 'Pay Downpayment'
      if (paymentType === 'bookingfee') return 'Pay Booking Fee'
      return 'Full Payment'
    }

    const needTypeLabel = needType => {
      if (needType === 'PRE-NEED') return 'Pre-need'
      if (needType === 'AS-NEED') return 'As-need'
      return needType || '–'
    }

    const toast = useToast()
    const showRequiredInfoModal = ref(false)
    const savingRequiredInfo = ref(false)

    const saveRequiredInformation = formData => {
      savingRequiredInfo.value = true
      store.dispatch('app-sales-submissions/updateItemRequiredInformation', {
        submissionId: submission.value._id,
        itemId: activeItem.value._id,
        formData,
      })
        .then(response => {
          Object.assign(submission.value.items[activeItemIndex.value], response.data)
          showRequiredInfoModal.value = false
          Vue.swal({
            title: 'Required Information Updated',
            html: '',
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary mt-1',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingRequiredInfo.value = false
        })
    }

    // serviceAgent is submission-level - saving here updates the whole submission object (every
    // item in it shares this same field), not just the active item.
    const showServiceAgentModal = ref(false)
    const savingServiceAgent = ref(false)

    const saveServiceAgent = ({ agentId }) => {
      savingServiceAgent.value = true
      store.dispatch('app-sales-submissions/updateServiceAgent', { id: submission.value._id, agentId })
        .then(response => {
          Object.assign(submission.value, response.data)
          showServiceAgentModal.value = false
          Vue.swal({
            title: 'Agent Details Updated',
            html: "Please send this back to the newly assigned agent for further action by clicking the 'Send back to Sales Agent' button.",
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingServiceAgent.value = false
        })
    }

    // Sales Agent (the submitting agent) - same submission-level shape as Service Agent above.
    // Confirmed-Agent only (see ServiceAgentChangeModal's own results.filter for the Agent-only
    // restriction) - reassigning here always sets mainAgent, never the ProbationaryAgent agent ref.
    const showSalesAgentModal = ref(false)
    const savingSalesAgent = ref(false)

    const saveSalesAgent = ({ agentId }) => {
      savingSalesAgent.value = true
      store.dispatch('app-sales-submissions/updateSalesAgent', { id: submission.value._id, agentId })
        .then(response => {
          Object.assign(submission.value, response.data)
          showSalesAgentModal.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Sales Agent Updated',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingSalesAgent.value = false
        })
    }

    // Purchaser Details is also submission-level - saving here updates the whole submission
    // (shared across every item), same as Service Agent.
    const showPurchaserDetailsModal = ref(false)
    const savingPurchaserDetails = ref(false)

    const savePurchaserDetails = formData => {
      savingPurchaserDetails.value = true
      store.dispatch('app-sales-submissions/updatePurchaserDetails', { id: submission.value._id, formData })
        .then(response => {
          Object.assign(submission.value, response.data)
          showPurchaserDetailsModal.value = false
          Vue.swal({
            title: 'Purchaser Details Updated',
            html: '',
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingPurchaserDetails.value = false
        })
    }

    // intendedUsers is item-level (each item has its own array) - saving here only updates the
    // one nominee within the currently active item, never sibling items or the submission itself.
    const showIntendedUserModal = ref(false)
    const savingIntendedUser = ref(false)
    const editingIntendedUser = ref(null)

    const openIntendedUserModal = user => {
      editingIntendedUser.value = user
      showIntendedUserModal.value = true
    }

    const saveIntendedUser = formData => {
      savingIntendedUser.value = true
      store.dispatch('app-sales-submissions/updateItemIntendedUser', {
        submissionId: submission.value._id,
        itemId: activeItem.value._id,
        userId: editingIntendedUser.value._id,
        formData,
      })
        .then(response => {
          Object.assign(submission.value.items[activeItemIndex.value], response.data)
          showIntendedUserModal.value = false
          Vue.swal({
            title: 'Intended User Updated',
            html: '',
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingIntendedUser.value = false
        })
    }

    // authorizedPersons is item-level (each item has its own array) - saving here only updates
    // the one person within the currently active item, never sibling items or the submission.
    const showAuthorizedPersonModal = ref(false)
    const savingAuthorizedPerson = ref(false)
    const editingAuthorizedPerson = ref(null)

    const openAuthorizedPersonModal = person => {
      editingAuthorizedPerson.value = person
      showAuthorizedPersonModal.value = true
    }

    const saveAuthorizedPerson = formData => {
      savingAuthorizedPerson.value = true
      store.dispatch('app-sales-submissions/updateItemAuthorizedPerson', {
        submissionId: submission.value._id,
        itemId: activeItem.value._id,
        userId: editingAuthorizedPerson.value._id,
        formData,
      })
        .then(response => {
          Object.assign(submission.value.items[activeItemIndex.value], response.data)
          showAuthorizedPersonModal.value = false
          Vue.swal({
            title: 'Authorised Person Updated',
            html: '',
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingAuthorizedPerson.value = false
        })
    }

    // Installment terms are item-level - saving here only updates the currently active item,
    // never sibling items or the submission itself. installmentAmount is never sent - it's
    // permanently read-only via this modal.
    const showInstallmentModal = ref(false)
    const savingInstallment = ref(false)

    const saveInstallment = formData => {
      savingInstallment.value = true
      store.dispatch('app-sales-submissions/updateItemInstallment', {
        submissionId: submission.value._id,
        itemId: activeItem.value._id,
        formData,
      })
        .then(response => {
          Object.assign(submission.value.items[activeItemIndex.value], response.data)
          showInstallmentModal.value = false
          Vue.swal({
            title: 'Installment Details Updated',
            html: '',
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingInstallment.value = false
        })
    }

    // promotions is item-level - saving here only updates the currently active item's own applied
    // promotion set, never sibling items or the submission itself.
    const showPromotionsModal = ref(false)
    const savingPromotions = ref(false)

    const savePromotions = formData => {
      savingPromotions.value = true
      store.dispatch('app-sales-submissions/updateItemPromotions', {
        submissionId: submission.value._id,
        itemId: activeItem.value._id,
        formData,
      })
        .then(response => {
          Object.assign(submission.value.items[activeItemIndex.value], response.data)
          showPromotionsModal.value = false
          Vue.swal({
            title: 'Discounts Updated',
            html: '',
            // eslint-disable-next-line global-require
            imageUrl: require('@/assets/images/icons/success.png'),
            imageWidth: 80,
            imageHeight: 80,
            imageAlt: 'Custom Icon',
            showCancelButton: false,
            confirmButtonText: 'Okay',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: (error.data && error.data.message) || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          savingPromotions.value = false
        })
    }

    return {
      submission,
      activeItemIndex,
      activeItem,
      salesAgentDetail,
      salesAgentType,
      permanentAddress,
      correspondenceAddress,
      itemSubtotal,
      itemOrderTotal,
      purchaseOptionLabel,
      needTypeLabel,
      canViewThisAction,
      resolvePaymentStatusVariant,
      resolvePaymentStatusLabel,
      isNricRevealed,
      revealNric,
      hideNric,
      showRequiredInfoModal,
      savingRequiredInfo,
      saveRequiredInformation,
      showServiceAgentModal,
      savingServiceAgent,
      saveServiceAgent,
      showSalesAgentModal,
      savingSalesAgent,
      saveSalesAgent,
      showPurchaserDetailsModal,
      savingPurchaserDetails,
      savePurchaserDetails,
      showIntendedUserModal,
      savingIntendedUser,
      editingIntendedUser,
      openIntendedUserModal,
      saveIntendedUser,
      showAuthorizedPersonModal,
      savingAuthorizedPerson,
      editingAuthorizedPerson,
      openAuthorizedPersonModal,
      saveAuthorizedPerson,
      showInstallmentModal,
      savingInstallment,
      saveInstallment,
      showPromotionsModal,
      savingPromotions,
      savePromotions,
    }
  },
}
</script>

<style lang="scss" scoped>
.submission-item-pill-inactive {
  background-color: #F5F8FB;
}
// position:relative (not static) is load-bearing here: .submission-detail-body::before is
// position:absolute, and CSS always paints positioned elements above static siblings regardless
// of DOM order - a static h4 renders BEHIND that ::before color band no matter its z-index.
.submission-item-title {
  position: relative !important;
  z-index: 1;
  font-family: Montserrat, Helvetica, Arial, serif;
  font-weight: 500;
  font-size: 28px;
  line-height: 100%;
  letter-spacing: 0px;
  color: #000000;
}
// Same technique as .without__bg.extra__bg (see _phase3b.scss) - a bled ::before positioned
// a little way down from the top, so the pill-button row sits half above/half below the color
// change instead of the whole row floating fully inside a flat-colored block.
.submission-detail-body {
  padding: 8px 24px 24px;
  margin-bottom: 1rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -100px;
    right: -100px;
    top: 27px;
    bottom: 0;
    background: #D2DCEA;
  }
}
// .content-body .card { padding: 0 !important; } wins on specificity/!important otherwise.
.dashboard__tab-content-card.card {
  gap: 20px;
  padding-top: 20px !important;
  padding-right: 40px !important;
  padding-bottom: 0 !important;
  padding-left: 40px !important;
  margin-bottom: 1rem !important;
}
</style>

<style lang="scss">
// This page has 4 top-level tabs, not the 3 the base .dashboard__tabs .nav-item width assumes
// (see _dashboard.scss) nor the 2 .file_detail overrides to (_calllog.scss) - needs its own.
.dashboard__tabs.submission-detail-tabs .nav-pills .nav-item {
  width: 25%;
}

// Base .dashboard__tabs fades inactive tabs via opacity instead of a distinct color - this page
// wants a real color swap instead: white for inactive, #F8F8F8 for the active one. The card (not
// the nav-link it sits inside) is what's actually visible, so the active override targets it.
.dashboard__tabs.submission-detail-tabs .dashboard__tab-card {
  opacity: 1;
  background-color: #FFFFFF;
}
.dashboard__tabs.submission-detail-tabs .nav-pills .nav-link.active .dashboard__tab-card {
  background-color: #F8F8F8;
}

// .nav-pills, .nav-tabs .nav-link svg { margin-right: .5rem } (_nav.scss) assumes an icon sitting
// directly inline next to text - here the icon is centered inside its own b-avatar circle, so
// that margin just pushes it off-center within the circle.
.dashboard__tabs.submission-detail-tabs .nav-pills .nav-link .b-avatar svg {
  margin-right: 0;
}

// .purchaser-tabs (_calllog.scss) gives its active nav-link a white background box - this design
// wants a plain underline for the Sales Agent/Service Agent switch, no boxed pill behind it.
.dashboard__tab-content-card .purchaser-tabs .nav-tabs .nav-link.active {
  background-color: transparent;
}

// .has-padding (_calllog.scss) already puts 20px padding on the .card itself; bootstrap-vue's
// auto-generated .card-body (no-body isn't set on these) stacks its own default padding on top,
// doubling the gap under the title - same fix as AgentDetailCard.vue's .agent__details override.
.dashboard__tab-content-card .card.has-padding .card-body {
  padding: 0 !important;
}
.order-info-card {
  margin-top: 1.5rem;
  margin-bottom: 3rem;
}
// Row-level margin, not per-card - keeps match-height's column-stretch math (Purchase
// Option/Discounts stack vs Price Details/Installment Details' h-100) undisturbed.
.payment-info-top-row {
  margin-top: 1.5rem;
  margin-bottom: 0;
}

// .content-body .card.has-padding th { padding: 0 !important; } (_common.scss) otherwise wins -
// non-scoped (not the scoped block above) so it isn't relying on the data-v attribute reaching
// through b-table-simple/b-thead/b-th's own internal rendering.
.content-body .card.has-padding .payment-history-table .table {
  th,
  td {
    padding: 1rem 0.75rem !important;
    vertical-align: middle;
  }
}

// .nav-item .nav-link .feather-dollar-sign { border: 2px solid white; } (_phase3b.scss) puts a
// round white border on any $-icon inside a nav-link - this page's Payment Information tab title
// icon shouldn't have it.
.dashboard__tabs.submission-detail-tabs .nav-pills .nav-link .feather-dollar-sign {
  border: none !important;
}

.nominee-card {
  background-color: #F5F8FB;
  border: none;

  .card-body {
    padding: 0.85rem;
  }
}

.singpass-badge {
  padding: 4px;
  background-color: #D7E5FF !important;
  border: 1px solid #D7E5FF;
  border-radius: 4px;
  opacity: 1;
  color: rgba(75, 108, 183, 1) !important;
  font-family: Open Sans;
  font-weight: 600 !important;
  font-size: 8px;
  line-height: 6px !important;
  letter-spacing: 0.25px;
  text-align: center;
}

.tab-bottom-card {
  margin-bottom: 3rem !important;
}
</style>
